import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import {
  BaseCell,
  Box,
  breakpoints,
  Button,
  CheckboxCell,
  Flex,
  FlexGrow,
  SearchInput,
  spacing,
  Stack,
  Text,
} from '@cash-design-system/react'
import {
  InvestmentEntity,
  InvestmentEntityCategory,
} from '@cash-web/protos/squareup/cash/marketdata/model/investment_entities.pb'

import { useInvestmentList } from './useInvestmentList'

const Icon = styled.img`
  display: block;
  flex-shrink: 0;
  height: 3rem;
  width: 3rem;
  border-radius: 50%;
`

const ClickableRow = styled.div`
  border-radius: 0.75rem;
  cursor: pointer;
  margin: ${({ theme: { spacing } }) => `-${spacing.m}`};
  padding: ${({ theme: { spacing } }) => `${spacing.m}`};

  :hover {
    background: ${({ theme }) => theme.colors.clickableContentBackgroundHover};
  }
`

const Container = styled(Flex)`
  height: 100%;
`

const EntityList = styled(Stack)`
  overflow-y: auto;
  margin: 0 -${spacing.l};
  padding: ${spacing.m} ${spacing.l};
`

const hasValidProps = (
  entity: InvestmentEntity
): entity is InvestmentEntity & { display_name: string; symbol: string; icon_url: string; token: string } =>
  entity.display_name !== undefined &&
  entity.symbol !== undefined &&
  entity.icon_url !== undefined &&
  entity.token !== undefined

type InvestmentItemsProps = {
  allInvestmentEntities: InvestmentEntity[]
  entities: InvestmentEntity[]
  handleInvestmentSelection: (token: string, isChecked?: boolean) => void
  maxSelections?: number
  multiSelect?: {
    selectedEntityTokens?: Set<string>
  }
}
const InvestmentItems = ({
  allInvestmentEntities,
  entities,
  handleInvestmentSelection,
  maxSelections,
  multiSelect,
}: InvestmentItemsProps) => {
  const { t } = useTranslation()
  const mappedEntities = entities.map(
    entity => allInvestmentEntities.find(allInvestmentEntity => allInvestmentEntity.token === entity.token) ?? entity
  )
  if (mappedEntities.length === 0) return null

  return (
    <Flex flexDirection="column" gap={spacing.xl}>
      {mappedEntities.filter(hasValidProps).map(({ display_name, symbol, icon_url, token }) => {
        const isChecked = multiSelect?.selectedEntityTokens ? multiSelect.selectedEntityTokens.has(token) : false
        const isMultiSelect = Boolean(multiSelect)

        return isMultiSelect ? (
          <CheckboxCell
            disabled={multiSelect?.selectedEntityTokens?.size === maxSelections && !isChecked}
            cellProps={{
              asset: {
                avatar: (
                  <Icon
                    src={icon_url}
                    alt={t('{display_name} logo', { context: 'alt text for logo image', display_name })}
                  />
                ),
              },
              body: symbol,
            }}
            label={display_name}
            checked={isChecked}
            onChange={() => {
              handleInvestmentSelection(token, !isChecked)
            }}
            key={token}
          />
        ) : (
          <ClickableRow
            key={token}
            onClick={() => {
              handleInvestmentSelection(token)
            }}
          >
            <BaseCell
              asset={{
                avatar: (
                  <Icon
                    src={icon_url}
                    alt={t('{display_name} logo', { context: 'alt text for logo image', display_name })}
                  />
                ),
              }}
              label={display_name}
              body={[symbol]}
              accessory={<Button size="small">{t('Select', { context: 'select investment button' })}</Button>}
            />
          </ClickableRow>
        )
      })}
    </Flex>
  )
}

const SearchInputContainer = styled.div`
  background-color: ${({ theme }) => theme.arcadeColors.component.modalBackground};
  position: sticky;
  top: 0;
  z-index: 1;
  margin: 0 -${spacing.l} ${spacing.l} -${spacing.l};

  ${breakpoints.tablet} {
    margin: 0 -${spacing.l};
    padding: 0 ${spacing.l} ${spacing.l} ${spacing.l};
  }
`

export interface InvestmentListProps {
  categories?: InvestmentEntityCategory[]
  handleInvestmentSelection: (token: string, isChecked?: boolean) => void
  searchBarPlaceholderText?: string
  allInvestmentEntities: InvestmentEntity[]
  maxSelections?: number
  multiSelect?: { selectedEntityTokens?: Set<string> }
}
export const InvestmentList = ({
  categories = [],
  handleInvestmentSelection,
  searchBarPlaceholderText = 'Search for a stock or ETF',
  allInvestmentEntities,
  multiSelect,
  maxSelections = 5,
}: InvestmentListProps) => {
  const { t } = useTranslation()
  const { clearSearch, searchValue, searchResults, handleSearchOnChange, categorizedEntities } = useInvestmentList({
    allInvestmentEntities,
    categories,
    multiSelect,
  })

  if (allInvestmentEntities.length === 0) {
    return (
      <Flex flexDirection="column">
        <Text typography="body" color="textDanger">
          {t('We ran into a problem, please try again.', {
            context: 'Error text displayed inline if investment list is empty',
          })}
        </Text>
      </Flex>
    )
  }

  return (
    <Container flexDirection="column">
      <SearchInputContainer>
        <SearchInput
          placeholder={searchBarPlaceholderText}
          name="search"
          onChange={handleSearchOnChange}
          value={searchValue}
        />
      </SearchInputContainer>
      <EntityList space={spacing.xl} data-testid="investment-entity-list">
        {searchValue ? (
          <Box padding={`${spacing.m} 0`}>
            <InvestmentItems
              allInvestmentEntities={allInvestmentEntities}
              entities={searchResults}
              handleInvestmentSelection={(token, isChecked) => {
                handleInvestmentSelection(token, isChecked)
                clearSearch()
              }}
              multiSelect={multiSelect}
              maxSelections={maxSelections}
            />
          </Box>
        ) : (
          Object.entries(categorizedEntities).map(([name, entities]) => (
            <Stack key={name} space={spacing.xl}>
              <Flex gap={spacing.m} alignItems="baseline">
                <FlexGrow>
                  <Text typography="sectionTitle" color="textStandard">
                    {name}
                  </Text>
                </FlexGrow>
                {name === 'Selected' && (
                  <Text typography="body" color="textSubtle">
                    {multiSelect?.selectedEntityTokens?.size}/{maxSelections}
                  </Text>
                )}
              </Flex>
              <InvestmentItems
                allInvestmentEntities={allInvestmentEntities}
                entities={entities}
                multiSelect={multiSelect}
                handleInvestmentSelection={handleInvestmentSelection}
                maxSelections={maxSelections}
              />
            </Stack>
          ))
        )}
      </EntityList>
    </Container>
  )
}

export default InvestmentList
